import React, { memo, useCallback, useEffect, useState } from 'react';
import { Editable, Slate, ReactEditor, withReact, RenderLeafProps } from 'slate-react';
import { BaseEditor, Node, Element, Text, Transforms, Range, Editor, createEditor } from 'slate';
import { PromptTemplateVar } from '../../api';

export type CustomElement = { type: "paragraph", children: CustomText[] }
export type CustomText = { text: string, var_id?: string, element_id?: string }

declare module 'slate' {
    interface CustomTypes {
        Element: CustomElement
        Text: CustomText
    }
}

interface SlateEditorProps {
    editor: BaseEditor & ReactEditor
    vars: PromptTemplateVar[];
    onReqPromptChange: (prompt: string) => void;
}

export const ReflectToEditor = (request_prompt: string, editor: BaseEditor & ReactEditor, vars: PromptTemplateVar[]) => {
    // リクエストプロンプトをパースして、editorに反映する
    const paragraphs = request_prompt.split('\n')
    const editorValue: CustomElement[] = paragraphs.map(paragraph => {
        // {{uuid}}パターンを見つけて置換
        const regex = /\{\{([^}]+)\}\}/g
        const children: CustomText[] = []
        let lastIndex = 0
        let match
        while ((match = regex.exec(paragraph)) !== null) {
            // マッチ前のテキスト
            if (match.index > lastIndex) {
                const beforeText = paragraph.slice(lastIndex, match.index)
                if (beforeText) {
                    children.push({ text: beforeText })
                }
            }
            // {{uuid}}部分の処理
            const varId = match[1]
            const foundVar = vars.find(v => v.id === varId)
            if (foundVar) {
                children.push({ 
                    text: foundVar.name, 
                    var_id: varId, 
                    element_id: window.crypto.randomUUID() 
                })
            } else {
                // varsに存在しない場合は通常のテキストとして扱う
                children.push({ text: match[0] })
            }
            lastIndex = match.index + match[0].length
        }
        // 残りのテキスト
        if (lastIndex < paragraph.length) {
            const remainingText = paragraph.slice(lastIndex)
            if (remainingText) {
                children.push({ text: remainingText })
            }
        }
        // 空の段落の場合は空のテキストノードを追加
        if (children.length === 0) {
            children.push({ text: '' })
        } else {
            // 変数要素の間や後にカーソル移動を可能にするための処理
            const processedChildren: CustomText[] = []
            for (let i = 0; i < children.length; i++) {
                const child = children[i]
                processedChildren.push(child)
                
                // 変数要素の後に、次の要素が変数要素または末尾の場合は空白文字を追加
                if (child.var_id) {
                    const nextChild = children[i + 1]
                    if (!nextChild || nextChild.var_id) {
                        processedChildren.push({ text: '\u200B' })
                    }
                }
            }
            children.splice(0, children.length, ...processedChildren)
        }
        return { type: "paragraph", children }
    })
    // Slateの適切な方法でエディタの内容を設定
    Editor.withoutNormalizing(editor, () => {
        // 既存のコンテンツをクリア
        Transforms.delete(editor, {
            at: {
                anchor: Editor.start(editor, []),
                focus: Editor.end(editor, [])
            }
        })
        
        // 新しいコンテンツを挿入
        if (editorValue.length > 0) {
            Transforms.insertNodes(editor, editorValue, { at: [0] })
        } else {
            // 空の場合はデフォルトの段落を挿入
            Transforms.insertNodes(editor, { type: "paragraph", children: [{ text: "" }] }, { at: [0] })
        }
    })
}


export const SlateEditor = memo<SlateEditorProps>(({ vars, onReqPromptChange, editor }) => {
    useEffect(() => {
        handleChange(vars)
    }, [vars])
    const renderLeaf = useCallback((props: RenderLeafProps) => {
        const {attributes, children, leaf} = props
        const v = vars.filter((v) => v.id == leaf.var_id).at(0)
        // 変数要素の場合
        if (leaf.var_id && leaf.element_id && v) {
            vars.filter((v) => v.id == leaf.var_id)
            const id = leaf.var_id
            const element_id = leaf.element_id
            return <span
                id={element_id}
                var-id={leaf.var_id}
                contentEditable={false}
                draggable
                onDragStart={(e) => {
                    e.dataTransfer.setData("text/plain", JSON.stringify({text: v.name, var_id: id, element_id: element_id}))
                }}
                style={{
                    backgroundColor: "#ffffff", 
                    border: "solid lightblue 2px",
                    borderRadius: "8px", 
                    padding: "8px",
                    cursor: "pointer",
                    display: "inline-block", // inline-blockに変更
                    margin: "2px 2px", // 左右に少しマージンを追加
                }}
                >
                {children}
            </span>
        }
        return <span {...attributes}>{children}</span>
    }, [vars])


    const handleChange = useCallback((vars: PromptTemplateVar[]) => {
        const reqs: string[] = []
        
        // エディターのルートレベルの段落のみを取得（Slateが自動追加する空の段落を避けるため）
        const rootChildren = editor.children
        
        for (let i = 0; i < rootChildren.length; i++) {
            const node = rootChildren[i]
            if (Element.isElement(node) && node.type === "paragraph") {
                const paragraphContent: string[] = []
                
                for (const child of node.children) {
                    if (Text.isText(child)) {
                        if (child.var_id && vars.filter((v) => v.id == child.var_id).length != 0) {
                            paragraphContent.push(`{{${child.var_id}}}`)
                        }
                        else {
                            const cleanText = child.text.replace(/\u200B/g, '')
                            paragraphContent.push(cleanText)
                        }
                    }
                }
                
                const joinedContent = paragraphContent.join('')
                
                // 最後の段落が空で、かつ他に段落がある場合は除外（Slateが自動追加した可能性）
                const isLastParagraph = i === rootChildren.length - 1
                const hasOtherParagraphs = rootChildren.length > 1
                
                if (isLastParagraph && hasOtherParagraphs && joinedContent.trim() === '') {
                    // 最後の空の段落は除外（Slateの自動追加を想定）
                    continue
                }
                
                reqs.push(joinedContent)
            }
        }
        onReqPromptChange(reqs.join("\n"))
    }, [editor, onReqPromptChange]);

    const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
        if (e.key === 'Backspace' || e.key === 'Delete') {
            const {selection} = editor
            if (selection && Range.isCollapsed(selection)) {
                const point = e.key === 'Backspace' 
                ? Editor.before(editor, selection.anchor, {unit: "offset"})
                : Editor.after(editor, selection.anchor, {unit: "offset"})
                const [node, path] = point ? Editor.node(editor, point.path) : Editor.node(editor, selection.anchor.path)

                if (Text.isText(node) && node.var_id && node.element_id) {
                    e.preventDefault()
                    Transforms.removeNodes(editor, {at: path})
                }
            }
        }
    }, [editor]);

    const handleDrop = useCallback((e: React.DragEvent) => {
        const domRange = document.caretRangeFromPoint(e.clientX, e.clientY)
        if(!domRange) return
        const slateRange = ReactEditor.toSlateRange(editor, domRange, {exactMatch: true, suppressThrow: true})
        if(!slateRange) return
        // 挿入前にキャレット位置を指定
        Transforms.select(editor, slateRange)
        const text = e.dataTransfer.getData("text/plain")
        try {
            const id_dict: CustomText = JSON.parse(text)
            if(id_dict.var_id && !id_dict.element_id) {
                // 上の変数リストからのドロップは、無条件で追加
                Transforms.insertNodes(editor, {text: id_dict.text, var_id: id_dict.var_id, element_id: window.crypto.randomUUID()})
                // 変数要素の後に空のテキストノードを追加（カーソル移動を可能にするため）
                Transforms.insertNodes(editor, {text: '\u200B'})
                e.preventDefault()
            }
            else if(id_dict.var_id && id_dict.element_id) {
                // この領域内でのD&Dなので、削除してから追加
                for (const [node, path] of Node.nodes(editor)) {
                    if(Text.isText(node) && node.var_id === id_dict.var_id && node.element_id === id_dict.element_id) {
                        Transforms.delete(editor, {at: path})
                    }
                }
                Transforms.insertNodes(editor, {text: id_dict.text, var_id: id_dict.var_id, element_id: id_dict.element_id})
                // 変数要素の後に空のテキストノードを追加（カーソル移動を可能にするため）
                Transforms.insertNodes(editor, {text: '\u200B'})
                e.preventDefault()
            }
        }
        catch {}
    }, [editor]);

    return (
        <Slate 
            editor={editor} 
            initialValue={[{type: "paragraph", children: [{text: ""}]}]}
            onChange={handleChange.bind(null, vars)}
        >
            <Editable 
                style={{
                    width: "500px", 
                    height: "300px",
                    backgroundColor: "white",
                    border: "solid black 1px",
                    outline: "none",
                    padding: "4px 8px",
                    overflowY: "auto",
                    marginTop: "0px",
                    fontSize: "10.5pt"
                }}
                autoFocus={false}
                onFocus={(e) => e.currentTarget.style.border = "solid #0078d4 2px"}
                onBlur={(e) => e.currentTarget.style.border = "solid black 1px"}
                renderLeaf={renderLeaf}
                onKeyDown={handleKeyDown}
                onDrop={handleDrop}
            />
        </Slate>
    );
});